import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box, Typography, Divider, Stack, Button, FormControl, Input, FormLabel, Chip, CircularProgress, IconButton, Tooltip } from '@mui/joy';
import { Card } from '@mui/joy';
import { useState, useEffect } from 'react';
import { useFeedback } from '../feedback/Service';
import { parseGraphQLError } from '../../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getProvider } from './providers';
import { getProviderQueries } from './providerQueryMap';
import { GET_GREENHOUSE_OAUTH_URL } from './queries';

const ATSConnection = ({ providerId = 'ashby' }) => {
  const [apiKey, setApiKey] = useState("");
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const feedback = useFeedback();
  const provider = getProvider(providerId);
  const pq = getProviderQueries(providerId);

  // Handle OAuth callback (Greenhouse)
  useEffect(() => {
    if (providerId !== 'greenhouse') return;
    const params = new URLSearchParams(window.location.search);
    const greenhouseParam = params.get('greenhouse');
    if (greenhouseParam === 'connected') {
      feedback.snackbar({ text: `${provider.name} connected successfully!`, type: "success" });
      // Clear URL params
      const url = new URL(window.location);
      url.searchParams.delete('greenhouse');
      window.history.replaceState({}, '', url);
      refetchStatus();
    } else if (greenhouseParam === 'error') {
      feedback.snackbar({ text: `Failed to connect ${provider.name}. Please try again.`, type: "error" });
      const url = new URL(window.location);
      url.searchParams.delete('greenhouse');
      window.history.replaceState({}, '', url);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: statusData, loading: statusLoading, error: statusError, refetch: refetchStatus } = useQuery(pq.GET_STATUS, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error(`Error fetching ${provider.name} status:`, error);
    }
  });

  // API key connect mutation (Ashby only - Greenhouse uses OAuth)
  const [connect, { loading: connecting }] = useMutation(pq.CONNECT, {
    onCompleted: () => {
      refetchStatus();
      setShowApiKeyInput(false);
      setApiKey("");
      feedback.snackbar({ text: `${provider.name} connected successfully!`, type: "success" });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  });

  // OAuth URL query (Greenhouse only)
  const [getOAuthUrl, { loading: loadingOAuthUrl }] = useLazyQuery(GET_GREENHOUSE_OAUTH_URL, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const url = data?.getGreenhouseOAuthUrl?.url;
      if (url) {
        window.location.href = url;
      } else {
        feedback.snackbar({ text: 'Failed to get authorization URL. Please try again.', type: 'error' });
      }
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  });

  const [disconnect, { loading: disconnecting }] = useMutation(pq.DISCONNECT, {
    onCompleted: () => {
      refetchStatus();
      feedback.snackbar({ text: `${provider.name} disconnected successfully!`, type: "success" });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
  });

  const handleDisconnect = async () => {
    const confirmed = await feedback.confirm({
      title: `Disconnect ${provider.name}?`,
      description: 'This will remove the integration. You can reconnect at any time.',
    });
    if (confirmed) {
      disconnect();
    }
  };

  const isConnected = statusData?.[pq.statusField]?.enabled;
  const connectedAt = statusData?.[pq.statusField]?.connectedAt;

  const apiKeyField = provider.fields?.find(f => f.name === 'apiKey');

  // Assessment API Key management (both Ashby and Greenhouse)
  const hasAssessmentSupport = !!pq.GET_ASSESSMENT_API_KEY;

  const { data: assessmentKeyData, refetch: refetchAssessmentKey } = useQuery(pq.GET_ASSESSMENT_API_KEY || pq.GET_STATUS, {
    skip: !hasAssessmentSupport || !isConnected,
    fetchPolicy: 'network-only',
  });

  const [generateAssessmentKey, { loading: generatingKey }] = useMutation(pq.GENERATE_ASSESSMENT_API_KEY || pq.DISCONNECT, {
    skip: !hasAssessmentSupport,
    onCompleted: () => {
      refetchAssessmentKey();
      feedback.snackbar({ text: 'Assessment API key generated successfully!', type: 'success' });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    },
  });

  const assessmentApiKey = hasAssessmentSupport ? assessmentKeyData?.[pq.assessmentKeyField] : null;

  const handleGenerateKey = async (isRegenerate) => {
    if (isRegenerate) {
      const confirmed = await feedback.confirm({
        title: 'Regenerate Assessment API Key?',
        description: `This will invalidate the current key. ${provider.name} will need to be updated with the new key.`,
      });
      if (!confirmed) return;
    }
    generateAssessmentKey();
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(assessmentApiKey);
    feedback.snackbar({ text: 'API key copied to clipboard!', type: 'success' });
  };

  return (
    <Card>
      <Box sx={{ mb: 1 }}>
        <Typography level="title-md" gutterBottom>ATS Integration — {provider.name}</Typography>
        <Typography level="body-sm">
          {provider.description || 'Connect your Applicant Tracking System to sync candidates.'}
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={2} sx={{ my: 2 }}>
        {statusLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm">Checking connection status...</Typography>
          </Box>
        ) : statusError ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'warning.softBg', borderRadius: 'sm' }}>
            <Typography level="body-sm" color="warning">
              Unable to check {provider.name} connection status. Please try again later.
            </Typography>
            <Button size="sm" variant="soft" color="warning" onClick={() => refetchStatus()}>
              Retry
            </Button>
          </Box>
        ) : isConnected ? (
          <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2, bgcolor: 'success.softBg', borderRadius: 'sm' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon color="success" />
              <Box>
                <Typography level="title-sm" sx={{ display: 'flex', alignItems: 'center', gap: 1 }} component="div">
                  {provider.name} Connected
                  <Chip size="sm" variant="soft" color="success">Active</Chip>
                </Typography>
                {connectedAt && (
                  <Typography level="body-xs" color="neutral">
                    Connected on {new Date(connectedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              size="sm"
              variant="plain"
              color="danger"
              loading={disconnecting}
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          </Box>

          {/* Assessment API Key Section */}
          {hasAssessmentSupport && (
            <Box sx={{ p: 2, bgcolor: 'neutral.softBg', borderRadius: 'sm' }}>
              <Typography level="title-sm" sx={{ mb: 1 }}>Assessment API Key</Typography>
              {assessmentApiKey ? (
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Input
                      size="sm"
                      value={assessmentApiKey}
                      readOnly
                      sx={{ flex: 1, fontFamily: 'monospace', fontSize: 'xs' }}
                    />
                    <Tooltip title="Copy to clipboard">
                      <IconButton size="sm" variant="plain" onClick={handleCopyKey}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography level="body-xs" color="neutral">
                    Copy this key and enter it in your {provider.name} assessment partner configuration (Settings → Integrations → Assessment Partners → Cavalry → API Key).
                  </Typography>
                  <Box>
                    <Button
                      size="sm"
                      variant="soft"
                      color="warning"
                      loading={generatingKey}
                      onClick={() => handleGenerateKey(true)}
                    >
                      Regenerate Key
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Typography level="body-sm" color="neutral">
                    No API key generated yet. Generate a key to enable {provider.name} Assessment integration.
                  </Typography>
                  <Box>
                    <Button
                      size="sm"
                      variant="solid"
                      color="primary"
                      loading={generatingKey}
                      onClick={() => handleGenerateKey(false)}
                    >
                      Generate Key
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          )}
          </>
        ) : (
          <Box>
            {provider.connectionType === 'oauth' ? (
              /* OAuth flow (Greenhouse) */
              <Button
                variant="outlined"
                color="primary"
                loading={loadingOAuthUrl}
                onClick={() => getOAuthUrl()}
              >
                Connect {provider.name}
              </Button>
            ) : (
              /* API Key flow (Ashby) */
              <>
                {!showApiKeyInput ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowApiKeyInput(true)}
                  >
                    Connect {provider.name}
                  </Button>
                ) : (
                  <Stack spacing={2}>
                    <FormControl>
                      <FormLabel>{apiKeyField?.label || 'API Key'}</FormLabel>
                      <Input
                        size="sm"
                        placeholder={apiKeyField?.placeholder || 'Enter your API key'}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        type="text"
                        autoComplete="off"
                        slotProps={{
                          input: {
                            autoComplete: 'off',
                            'data-lpignore': 'true',
                            'data-form-type': 'other',
                          }
                        }}
                      />
                      {apiKeyField?.helpText && (
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                          {apiKeyField.helpText}
                        </Typography>
                      )}
                    </FormControl>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        loading={connecting}
                        disabled={!apiKey.trim()}
                        onClick={() => connect({ variables: { apiKey } })}
                      >
                        Connect
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        onClick={() => {
                          setShowApiKeyInput(false);
                          setApiKey("");
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}
      </Stack>
    </Card>
  );
};

export default ATSConnection;
